import { useAuth } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { fetchPermissions } from "@/api/permissions";
import type { AccessScope } from "@/interfaces/permissions";

/** Scope ordering (narrow → wide), mirrors the backend SCOPE_RANK. */
const SCOPE_RANK: Record<AccessScope, number> = { own: 0, group: 1, org: 2, all: 3 };

/**
 * usePermissions — fetches the current user's resolved capability matrix (`caps`)
 * from the backend and provides a `can(action, subject, minScope?)` check.
 *
 * `caps` is membership-aware: subject → action → widest scope the caller holds
 * (union across all memberships). It is the same structure the backend enforces on,
 * so the UI gates on exactly what the API will allow.
 *
 * Fetched once after authentication and cached for the session (staleTime: Infinity);
 * the cache is cleared on sign-out when React Query resets with auth state.
 *
 * Usage:
 *   const { can, scopeFor, isLoaded } = usePermissions();
 *   if (can("delete", "CAMPAIGN")) { ... }
 *   if (can("view", "USER", "all")) { ...GFG-only surface... }
 */
export function usePermissions() {
  // Clerk's STRING id, deliberately named apart from the numeric app `userId` returned below:
  // it only keys the cache, so one browser session can never read another user's caps.
  const { isSignedIn, userId: clerkUserId } = useAuth();

  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: ["me", "permissions", clerkUserId],
    queryFn: fetchPermissions,
    enabled: !!isSignedIn,
    staleTime: Infinity,
    gcTime: Infinity,
    select: (data) => ({
      caps: data.caps ?? {},
      isGfg: data.isGfg ?? false,
      userId: data.userId ?? null,
      organizationId: data.organizationId ?? null,
      groupId: data.groupId ?? null,
      // The group to operate in when nobody has chosen one: the caller's own placement, else the
      // organisation's root group. Distinct from `groupId`, which is the placement itself and is
      // what scope decisions read — this is only a default to preselect.
      defaultGroupId: data.defaultGroupId ?? null,
    }),
  });

  const caps = data?.caps;
  const isGfg = data?.isGfg ?? false;
  const userId = data?.userId ?? null;
  const organizationId = data?.organizationId ?? null;
  const groupId = data?.groupId ?? null;
  const defaultGroupId = data?.defaultGroupId ?? null;

  /** The widest scope the caller holds for (action, subject), or undefined if not allowed. */
  const scopeFor = useCallback(
    (action: string, subject: string): AccessScope | undefined => caps?.[subject]?.[action],
    [caps],
  );

  /**
   * True if the caller may perform `action` on `subject`. When `minScope` is given,
   * also requires their scope for it to be at least that wide (e.g. "all" gates a
   * GFG-only surface).
   */
  const can = useCallback(
    (action: string, subject: string, minScope?: AccessScope): boolean => {
      const scope = caps?.[subject]?.[action];
      if (scope === undefined) return false;
      if (minScope && SCOPE_RANK[scope] < SCOPE_RANK[minScope]) return false;
      return true;
    },
    [caps],
  );

  return {
    /** Check capability (optionally requiring a minimum scope) for the given action/subject. */
    can,
    /** The widest scope held for an (action, subject), or undefined if not allowed. */
    scopeFor,
    /** True if the caller is GFG staff (holds a GFG/ALL-level membership). */
    isGfg,
    /** The caller's own numeric app user id — the seed for their generated avatar. */
    userId,
    /** The caller's own org id (for sending an explicit placement on create). */
    organizationId,
    /** The caller's own home-group id. */
    groupId,
    defaultGroupId,
    /** True once caps have been fetched successfully. */
    isLoaded: !isLoading && !!data,
    /**
     * The fetch finished and produced nothing usable — the request failed, or it is still to be
     * retried. Distinct from `isLoaded`, which only says caps ARRIVED: a caller that waits on
     * `isLoaded` alone waits forever when the call 401s, which is exactly what happens to a signed
     * in user whose local row does not exist yet.
     */
    isError,
    /** A request is in flight, including a refetch over data already cached. */
    isFetching,
  };
}
